import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeature,
  CharacterLanguageFeature,
  CharacterSense,
  Proficiency,
  CharacterFeatureSource,
  SpellsFromList,
  SFFeature,
  LanguageFeature,
  Selectable,
  BonusSpells,
  CharacterBonusSpells,
  CharacterSheetInterface,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import DataUtilities from '../../../utilities/common_utilities';

import CharacterASIBaseFeatureInput from "@dndComponents/character/features/CharacterASIBaseFeature";
import CharacterLanguageFeatureInput from "@dndComponents/character/misc/CharacterLanguageFeatureInput";
import CharacterEldritchInvocationsBox from "@dndComponents/character/misc/CharacterEldritchInvocationsBox";
import CharacterFightingStyleBox from "@dndComponents/character/misc/CharacterFightingStyleBox";
import CharacterSpecialFeatureBox from "@dndComponents/character/misc/CharacterSpecialFeatureBox";
import CharacterPactBoonBox from "@dndComponents/character/misc/CharacterPactBoonBox";
import CharacterFeatBox from "@dndComponents/character/feats/CharacterFeatBox";
import CharacterProficienciesInput from "@dndComponents/character/features/CharacterProficienciesInput";
import CharacterCharacteristicInput from "@dndComponents/character/features/CharacterCharacteristicInput";

import CharacterFeatureSourceInput from "@dndComponents/character/features/CharacterFeatureSourceInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSelectableInput from "@dndComponents/character/features/CharacterSelectableInput";
import CharacterWeaponMasteriesBox from "@dndComponents/character/misc/CharacterWeaponMasteriesBox";
import CharacterKnowledgeFeatureInput from "@dndComponents/character/knowledge/CharacterKnowledgeFeatureInput";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterSubclassInput from "@dndComponents/character/misc/CharacterSubclassInput";
import CharacterSubraceInput from "@dndComponents/character/misc/CharacterSubraceInput";
import CharacterSpellsFromListInput from "@dndComponents/character/features/CharacterSpellsFromListInput";
import CharacterBonusSpellsInput from "@dndComponents/character/features/CharacterBonusSpellsInput";
import CharacterExpertisesInput from "@dndComponents/character/features/CharacterExpertisesInput";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

interface Props {
  obj: CharacterFeature;
  source: CharacterFeatureSource;
  char: CharacterSheetInterface;
  show_source?: boolean;
}

export default function CharacterFeatureInput({ 
  obj,
  source,
  char,
  show_source = false,
}: Props) {
  const [show_source_modal, setShowSourceModal] = useState(false);

  const onChange = () => {
    char.changed();
    char.subscription_holder.recalculate();
  }

  // debugger;
  const enabled = api.user && (char.owner_id === api.user._id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id));
  if (obj.feature) {
    // switch based on feature type
    // debugger;
    let details = 
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={12} style={{ fontWeight: "bold" }}>
          { obj.feature.name }
        </Grid>
        <Grid size={12}>
          <DisplayStringWithReplacements
            display_string={obj.feature.description}
          />
        </Grid>
        { obj.feature_options.map((opt, key) => {
          if (opt.features) {
            return (
              <Grid size={12} key={key}
                container spacing={0} direction="row">
                { opt.features.map((cf: any, key2: any) => {
                  const char_feature = cf as CharacterFeature;
                  return (
                    <Grid size={12} key={key2}>
                      { char_feature.name } { char_feature.feature && 
                        <span><DisplayStringWithReplacements display_string={ char_feature.feature.description } /></span>
                      }
                    </Grid>
                  );
                })}
              </Grid>
            );
          } else {
            return null;
          }
        })}
      </Grid>;
    if (enabled) {
      switch(obj.feature_type) {
        case "Subclass":
          if (char instanceof Character) {
            details = (
              <Grid size={12}>
                <CharacterSubclassInput 
                  character={char} 
                  obj={obj} 
                  onChange={(changed: CharacterFeature) => {
                    onChange();
                  }}
                />
              </Grid>
            );
          }
        break;
        case "Subrace":
          if (char instanceof Character) {
            details = (
              <Grid size={12}>
                <CharacterSubraceInput 
                  character={char} 
                  obj={obj} 
                  onChange={(changed: CharacterFeature) => {
                    onChange();
                  }}
                />
              </Grid>
            );
          }
        break;
        case "Language":
          if (obj.feature_options[0] instanceof CharacterLanguageFeature && 
            obj.floating_feature instanceof LanguageFeature) {
            const clf = obj.feature_options[0];
            const lf = obj.floating_feature;
            // console.log(clf);
            // console.log(lf);
            details = 
              <Grid size={12}>
                <CharacterLanguageFeatureInput
                  char={char}
                  obj={obj}
                  color={clf.language_ids.length !== lf.count ? "blue" : ""}
                  onChange={(changed: CharacterLanguageFeature) => {
                    obj.feature_options[0] = changed;
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Specific Special Feature":
        case "Special Feature Choices":
        case "Special Feature":
          if (obj.floating_feature instanceof SFFeature) {
            const sff = obj.floating_feature;
            const spec_choices = sff.special_feature_ids;
            details = 
              <Grid size={12}>
                <CharacterSpecialFeatureBox
                  char={char}
                  type={sff.special_feature_type}
                  source_id={obj.use_source_id}
                  obj={obj}
                  options={spec_choices}
                  onChange={() => {
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Special Feature List Merge":
          details = 
            <Grid size={12}>
              You will have extra options available.
            </Grid>;
        break;
        case "Sense":
          const sense = obj.feature_options[0] as CharacterSense;
          if (sense) {
            details = 
              <Grid size={12}>
                { sense.name }: { sense.range }
              </Grid>;
          }
        break;
        case "Pact Boon":
          if (char instanceof Character) {
            details = 
              <Grid size={12}>
                <CharacterPactBoonBox
                  character={char}
                  obj={obj}
                  onChange={() => {
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Eldritch Invocations":
          if (char instanceof Character) {
            details = 
              <Grid size={12}>
                <CharacterEldritchInvocationsBox
                  character={char}
                  obj={obj}
                  onChange={() => {
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Weapon Mastery":
          // console.log("weapon mastery");
          if (char instanceof Character) {
            details = 
              <Grid size={12}>
                <CharacterWeaponMasteriesBox
                  character={char}
                  obj={obj}
                  onChange={() => {
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Fighting Style":
          if (char instanceof Character) {
            details = 
              <Grid size={12}>
                <CharacterFightingStyleBox
                  character={char}
                  obj={obj}
                  onChange={() => {
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        // case "Cantrips from List":
        case "Spells from List":
          if (obj.floating_feature && 
            obj.floating_feature instanceof SpellsFromList) {
            details = (
              <Grid size={12}>
                <CharacterSpellsFromListInput
                  char={char}
                  char_feature={obj}
                  onChange={onChange}
                />
              </Grid>
            );
          }
        break;
        case "Proficiencies":
          if (obj.floating_feature && 
            obj.floating_feature instanceof Proficiency) {
            // console.log(obj);
            details = (
              <>
                <Grid size={12}>
                  <CharacterProficienciesInput 
                    char={char}
                    char_feature={obj}
                  />
                </Grid>
              </>
            );
          }
        break;
        case "Expertise":
          if (obj.floating_feature && 
            obj.floating_feature instanceof Proficiency) {
            details = (
              <>
                <Grid size={12}>
                  <CharacterExpertisesInput 
                    char={char}
                    char_feature={obj}
                    onChange={() => {
                      onChange();
                    }}
                  />
                </Grid>
              </>
            );
          }
        break;
        case "Mystic Arcanum":
          details = 
            <Grid size={12}>
              { obj.feature_options.map((opt, key) => {
                return (
                  <span key={key}>Mystic Arcanum {opt.level}</span>
                );
              })}
            </Grid>;
        break;
        case "Spell Mastery":
          details = 
            <Grid size={12}>
              { obj.feature_options.map((opt, key) => {
                return (
                  <span key={key}>Spell Mastery {opt.level}</span>
                );
              })}
            </Grid>;
        break;
        case "Feat":
          if (char instanceof Character) {
            details = 
              <Grid size={12}>
                <CharacterFeatBox
                  char={char}
                  obj={obj}
                  onChange={() => {
                    // obj.feature_options[0] = changed;
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Selectable":
          if (obj.floating_feature instanceof Selectable && char instanceof Character) {
            details = 
              <Grid size={12}>
                <CharacterSelectableInput
                  character={char}
                  obj={obj} 
                  onChange={(changed: string[]) => {
                    obj.feature_options = changed;
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Ability Score Improvement":
          // console.log(obj);
          details = 
            <Grid size={12}>
              <CharacterASIBaseFeatureInput
                char={char}
                obj={obj}
              />
            </Grid>;
        break;
        case "Characteristic":
          if (char instanceof Character) {
            details = 
              <CharacterCharacteristicInput 
                character={char}
                char_feature={obj}
                // value={obj.feature_options[0]}
                onChange={(changed: string) => {
                  obj.feature_options[0] = changed;
                  onChange();
                }}
              />;
          }
        break;
        case "Knowledge":
          if (char instanceof Character) {
            details = 
              <CharacterKnowledgeFeatureInput 
                character={char} 
                obj={obj} 
                onChange={() => {
                  onChange();
                }}
              />;
          }
        break;
        case "Bonus Spells":
          if (obj.floating_feature && 
            obj.floating_feature instanceof BonusSpells) {
            details = (
              <Grid size={12}>
                <CharacterBonusSpellsInput
                  char={char}
                  char_feature={obj}
                  onChange={(changed: CharacterBonusSpells) => {
                    // debugger;
                    obj.feature_options = [changed];
                    onChange();
                  }}
                />
              </Grid>
            );
          }
        break;
        default:
          details = 
            <Grid
              size={12}>
              <b>{ obj.feature.name }</b> <DisplayStringWithReplacements display_string={ obj.feature.description } />
            </Grid>;
        break;
      }
    }
    return (
      <Grid container spacing={0} direction="row">
        { details }
        { show_source &&
          <Grid size={12}
            style={{ 
              fontWeight: "bold", 
              cursor: "pointer" 
            }}
            onClick={() => {
              setShowSourceModal(true);
            }}>
            { char_util.getUseName(source) }
          </Grid>
        }
        <ModalOrDrawer
          open={show_source_modal}
          onClose={() => { setShowSourceModal(false) }}
        >
          <Grid container spacing={0} direction="row">
            <Grid size={12}
              className="centered"
              style={{ fontWeight: "bold" }}>
              { char_util.getUseName(source) }
            </Grid>
            <Grid size={12}>
              <CharacterFeatureSourceInput
                char={char}
                source={source}
              />
            </Grid>
          </Grid>
        </ModalOrDrawer>
      </Grid>
    );
  }
  return <></>;
}
